import { useEffect, useState, useRef } from "react";
import { GatewayEnum } from "../../enums";
import { CustomerCardModel, StoreGatewayModel } from "../../models";
import { Form } from "react-bootstrap";
import { usePayment } from "../../context";
import { useAuth, useStore } from "../../hooks";
import { PayWithGoogle, PayWithApple, PayWithSavedCard } from "..";
import { isSafariBrowser } from "../../utilities";
import { GetSavedPayments } from "../../services";
import { formatAmount } from "../../helpers";
import clsx from "clsx";
import Skeleton from "react-loading-skeleton";
import { Toast } from "../Toast";

type Props = {
  total: number;
  gateway: StoreGatewayModel;
  handlePaymentModeChange: (gateway_id: GatewayEnum, mode: string) => void;
  isChecked: boolean;
};

const squareSdkCdn: string = import.meta.env.VITE_SQUAREUP_SDK_CDN;

const PayWithSquare: React.FC<Props> = ({
  isChecked = false,
  total,
  gateway,
  handlePaymentModeChange,
}) => {
  const [payments, setPayments] = useState<CustomerCardModel[]>([]);
  const [initError, setInitError] = useState(false);
  const {
    mode,
    setMode,
    card,
    setCard,
    saveCard,
    setSaveCard,
    savedCard,
    setSavedCard,
    squarePayments,
    setSquarePayments,
    setApplePay,
    setGooglePay,
  } = usePayment();
  const { isLoggedIn, customerId, isGuest } = useAuth();
  const { storeId } = useStore();

  const isInitializing = useRef(false);
  const initToken = useRef<{ cancelled: boolean }>({ cancelled: false });
  // Ref keeps the latest card value accessible in cleanup closures
  const cardRef = useRef<any>(null);

  useEffect(() => {
    if (isChecked && mode === "") setMode("card");
  }, [isChecked]);

  useEffect(() => {
    if (isLoggedIn && !isGuest) fetchSavedPayments();
  }, [isLoggedIn, isGuest]);

  useEffect(() => {
    // Wait until the card container is visible before initializing.
    // card.attach() fails on a display:none container.
    if (mode !== "card") return;

    initializeSquarePayment();

    return () => {
      if (cardRef.current && typeof cardRef.current.destroy === "function") {
        cardRef.current.destroy();
        cardRef.current = null;
      }
      const container = document.getElementById("card-container");
      if (container) container.innerHTML = "";
      initToken.current.cancelled = true;
      initToken.current = { cancelled: false };
      setCard(undefined);
      setSquarePayments(undefined);
      setInitError(false);
      isInitializing.current = false;
    };
    // eslint-disable-next-line
  }, [gateway, mode]);

  useEffect(() => {
    if (!mode || !squarePayments) return;

    if (mode === "googlepay") setupGooglePay();
    else if (mode === "applepay") setupApplePay();
  }, [squarePayments, mode, total]);

  const fetchSavedPayments = async () => {
    try {
      const { status, message, data } = await GetSavedPayments({
        store_id: storeId,
        customer_id: customerId,
      });
      if (status) setPayments(data);
      else Toast.error(message);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const loadSquareScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Square) {
        resolve(true);
        return;
      }
      const existingScript = document.querySelector('script[src*="square.js"]');
      if (existingScript) {
        // Attach listeners first, then re-check in case the script finished
        // loading in the gap between the window.Square check above and here.
        existingScript.addEventListener("load", () => resolve(true));
        existingScript.addEventListener("error", () =>
          reject(new Error("Failed to load Square.js")),
        );
        if (window.Square) resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = squareSdkCdn;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Failed to load Square.js"));
      document.body.appendChild(script);
    });
  };

  const initializeSquarePayment = async () => {
    if (isInitializing.current) return;
    isInitializing.current = true;
    setInitError(false);

    const token = initToken.current;

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error("Square initialization timed out")),
        15000,
      ),
    );

    try {
      await Promise.race([loadSquareScript(), timeout]);
      if (token.cancelled) return;

      if (!window.Square) throw new Error("Square.js not loaded");

      const container = document.getElementById("card-container");
      if (!container) throw new Error("Card container not found");
      container.innerHTML = "";

      const squarePayments = window.Square.payments(
        gateway.account_id,
        gateway.location_id,
      );

      const card = await Promise.race([squarePayments.card(), timeout]);
      if (token.cancelled) {
        card.destroy?.();
        return;
      }

      await card.attach("#card-container");
      if (token.cancelled) {
        card.destroy?.();
        return;
      }

      cardRef.current = card;
      setSquarePayments(squarePayments);
      setCard(card);
    } catch (error) {
      if (!token.cancelled) {
        console.error("Failed to initialize Square Payments:", error);
        setInitError(true);
      }
    } finally {
      isInitializing.current = false;
    }
  };

  const createPaymentRequest = () =>
    squarePayments?.paymentRequest({
      countryCode: "US",
      currencyCode: "USD",
      total: {
        label: "Total",
        amount: formatAmount(total),
      },
      requestBillingContact: false,
    });

  const handleModeChange = (mode: string) => {
    setMode(mode);
    handlePaymentModeChange(gateway.gateway_id, mode);
  };

  const setupGooglePay = async () => {
    try {
      const paymentRequest = createPaymentRequest();
      if (!paymentRequest) return;
      const googlePay = await squarePayments?.googlePay(paymentRequest);
      setGooglePay(googlePay);
    } catch (error: any) {
      if (error.name === "PaymentMethodUnsupportedError") {
        Toast.error(`Google Pay is unsupported: ${error.message}`);
      }
      console.error(error);
    }
  };

  const setupApplePay = async () => {
    try {
      const paymentRequest = createPaymentRequest();
      if (!paymentRequest) return;
      const applePay = await squarePayments?.applePay(paymentRequest);
      setApplePay(applePay);
    } catch (error: any) {
      if (error.name === "PaymentMethodUnsupportedError") {
        Toast.error(`Apple Pay is unsupported: ${error.message}`);
      }
      console.error(error);
    }
  };

  const handleSavedCardSelection = (card: CustomerCardModel) => {
    setSavedCard(card);
    handleModeChange("savedcard");
  };

  const handleCvvChange = (payment: CustomerCardModel) => {
    setSavedCard(payment);
    setPayments(
      payments.map((p) => (p.token_uid === payment.token_uid ? payment : p)),
    );
  };

  return (
    <>
      <PayWithSavedCard
        payments={payments}
        onSelect={handleSavedCardSelection}
        onCvvChange={handleCvvChange}
        selected={mode === "savedcard"}
        selectedCard={savedCard}
      />
      <div
        className={clsx(
          "card choose_payment_method_colm mb-2",
          isChecked && "payment-method-active",
        )}
      >
        <div className="card_viza card_viza2">
          <label>
            <span>
              <input
                type="radio"
                id="pay_with_square"
                name="payment_mode"
                onChange={() => handleModeChange("card")}
                checked={mode === "card"}
              />{" "}
              Credit Card
            </span>
          </label>
        </div>
        <div className={`px-5 py-1 ${mode === "card" ? "d-block" : "d-none"}`}>
          <div className="h6 card-detail-text">ENTER YOUR CARD DETAILS</div>
          {isChecked && !card && !initError && (
            <>
              <Skeleton height={40} className="mb-1" />
              <Skeleton height={40} className="mb-1" />
            </>
          )}

          {initError && (
            <div className="text-center py-2">
              <p className="text-danger small mb-2">
                Unable to load payment form. Please try again.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={initializeSquarePayment}
              >
                Retry
              </button>
            </div>
          )}

          <div id="card-container" className="card-bg"></div>
          <Form.Check
            className="mb-2"
            id="save_card_square"
            type="checkbox"
            name="save_card_square"
            size={24}
            label="Save this card for later?"
            checked={saveCard}
            onChange={(e) => setSaveCard(e.target.checked)}
          />
        </div>
      </div>

      {!isSafariBrowser() && squarePayments && (
        <PayWithGoogle
          isChecked={mode === "googlepay"}
          onChange={() => handleModeChange("googlepay")}
        />
      )}

      {isSafariBrowser() && squarePayments && (
        <PayWithApple
          isChecked={mode === "applepay"}
          onChange={() => handleModeChange("applepay")}
        />
      )}
    </>
  );
};

export default PayWithSquare;
